import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  CircularProgress,
  Dialog,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import UsbOutlinedIcon from "@mui/icons-material/UsbOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { BRANCHES } from "../WebleadsV3/utilities";
import {
  createSignageBranch,
  deleteSignageBranch,
  fetchSignageSettings,
  testSignageWeatherKey,
  updateSignageBranch,
  updateSignageSettings,
} from "./signageApi";
import {
  EmptyState,
  ErrorNote,
  ICON_BTN_SM,
  Loading,
  Pill,
  SearchInput,
  deviceIssues,
} from "./signageUi";

// Branches tab (Admin/Executive Team): the physical locations displays
// belong to — who can manage each (its General Manager), weather
// coordinates, and the shop clock board option (PCW / Mill). Plus the org-wide Settings dialog.

const hasCoords = (b) => b.latitude != null && b.longitude != null;

export const BranchesPanel = ({
  branches,
  devices,
  playlists,
  userId,
  onViewDisplays,
  onViewPlaylists,
  onChanged,
}) => {
  const [editing, setEditing] = useState(null); // null | "new" | branch
  const [search, setSearch] = useState("");

  const playlistById = useMemo(
    () => new Map((playlists || []).map((p) => [p.id, p])),
    [playlists]
  );

  const statsFor = (branch) => {
    const own = (devices || []).filter((d) => d.branch === branch.id);
    const offline = own.filter((d) => !d.online).length;
    const warn = own.filter(
      (d) => d.online && deviceIssues(d, playlistById).some((i) => i.attention)
    ).length;
    return {
      displays: own.length,
      online: own.length - offline,
      healthy: own.length - offline - warn,
      warn,
      offline,
      playlists: (playlists || []).filter((p) => p.branch === branch.id).length,
    };
  };

  const totals = (devices || []).reduce(
    (t, d) => ({ displays: t.displays + 1, online: t.online + (d.online ? 1 : 0) }),
    { displays: 0, online: 0 }
  );
  const missingWeather = branches.filter((b) => !hasCoords(b)).length;

  const query = search.trim().toLowerCase();
  const shown = branches.filter(
    (b) =>
      !query ||
      b.name.toLowerCase().includes(query) ||
      (b.userBranch || "").toLowerCase().includes(query) ||
      (b.locationLabel || "").toLowerCase().includes(query)
  );

  return (
    <div className="space-y-4">
      <div className="app-card space-y-4 rounded-2xl border p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="font-semibold tabular-nums">
              {branches.length} branch{branches.length === 1 ? "" : "es"}
              <span className="app-muted text-sm font-normal">
                {" "}· {totals.displays} display{totals.displays === 1 ? "" : "s"}, {totals.online} online
              </span>
            </p>
            <p
              className={`mt-0.5 text-xs ${
                missingWeather > 0 ? "text-amber-700 dark:text-amber-400" : "app-muted"
              }`}
            >
              {missingWeather > 0
                ? `${missingWeather} branch${missingWeather === 1 ? " has" : "es have"} no weather location yet.`
                : "Each branch is a location with its own displays, media, and playlists."}
            </p>
          </div>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setEditing("new")}
          >
            New branch
          </Button>
        </div>
        {branches.length > 6 && (
          <SearchInput
            placeholder="Search branches"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        )}
      </div>

      {branches.length === 0 ? (
        <EmptyState
          icon={<TvOutlinedIcon />}
          title="No signage branches yet"
          action={
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing("new")}>
              New branch
            </Button>
          }
        >
          Create a branch for each location with TVs, then register its
          displays on the Displays tab.
        </EmptyState>
      ) : shown.length === 0 ? (
        <EmptyState icon={<SearchOffIcon />} title="No branches match">
          Try a different search.
        </EmptyState>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((branch) => (
            <BranchCard
              key={branch.id}
              branch={branch}
              stats={statsFor(branch)}
              onEdit={() => setEditing(branch)}
              onViewDisplays={() => onViewDisplays(branch)}
              onViewPlaylists={() => onViewPlaylists(branch)}
            />
          ))}
        </div>
      )}

      {editing && (
        <BranchDialog
          branch={editing === "new" ? null : editing}
          stats={editing === "new" ? null : statsFor(editing)}
          userId={userId}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            onChanged();
          }}
        />
      )}
    </div>
  );
};

const SetupItem = ({ done, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition hover:bg-[color:var(--app-hover)]"
  >
    {done ? (
      <CheckCircleIcon className="text-emerald-500" sx={{ fontSize: 15 }} />
    ) : (
      <RadioButtonUncheckedIcon className="app-muted" sx={{ fontSize: 15 }} />
    )}
    <span className={done ? "" : "app-muted"}>{children}</span>
  </button>
);

const BranchCard = ({ branch, stats, onEdit, onViewDisplays, onViewPlaylists }) => {
  const pct = (n) => (stats.displays ? (n / stats.displays) * 100 : 0);
  const tone =
    stats.displays === 0
      ? "slate"
      : stats.offline === 0 && stats.warn === 0
        ? "emerald"
        : stats.online === 0
          ? "red"
          : "amber";
  return (
    <div className="app-card group flex flex-col rounded-2xl border transition hover:shadow-md">
      <div className="flex items-start gap-2 px-5 pt-5">
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-semibold tracking-tight" title={branch.name}>
            {branch.name}
          </p>
          {branch.locationLabel && (
            <p className="app-muted truncate text-xs">{branch.locationLabel}</p>
          )}
        </div>
        <Pill tone={tone}>
          {stats.displays === 0 ? "no displays" : `${stats.online}/${stats.displays} online`}
        </Pill>
        <button
          type="button"
          className={`${ICON_BTN_SM} -mr-1.5 -mt-1.5`}
          title="Edit branch"
          aria-label={`Edit ${branch.name}`}
          onClick={onEdit}
        >
          <EditOutlinedIcon sx={{ fontSize: 18 }} />
        </button>
      </div>

      {stats.displays > 0 && (
        <div className="mx-5 mt-3 flex h-1.5 overflow-hidden rounded-full bg-slate-500/15">
          <span className="bg-emerald-500" style={{ width: `${pct(stats.healthy)}%` }} />
          <span className="bg-amber-500" style={{ width: `${pct(stats.warn)}%` }} />
          <span className="bg-red-500" style={{ width: `${pct(stats.offline)}%` }} />
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-2 px-5">
        <button
          type="button"
          className="app-surface rounded-xl border px-3 py-2.5 text-left transition hover:border-sky-400"
          onClick={onViewDisplays}
          title="Open this branch's displays"
        >
          <p className="text-xl font-bold tabular-nums tracking-tight">{stats.displays}</p>
          <p className="app-muted text-xs">
            display{stats.displays === 1 ? "" : "s"}
            {stats.offline > 0 && (
              <span className="font-semibold text-red-600 dark:text-red-400"> · {stats.offline} offline</span>
            )}
          </p>
        </button>
        <button
          type="button"
          className="app-surface rounded-xl border px-3 py-2.5 text-left transition hover:border-sky-400"
          onClick={onViewPlaylists}
          title="Open this branch's playlists"
        >
          <p className="text-xl font-bold tabular-nums tracking-tight">{stats.playlists}</p>
          <p className="app-muted text-xs">playlist{stats.playlists === 1 ? "" : "s"}</p>
        </button>
      </div>

      <div className="app-divider mt-4 space-y-0.5 border-t px-3 pb-3 pt-2">
        <SetupItem done={!!branch.userBranch} onClick={onEdit}>
          {branch.userBranch ? `Managed by the ${branch.userBranch} GM` : "Admins only — no GM access"}
        </SetupItem>
        <SetupItem done={hasCoords(branch)} onClick={onEdit}>
          {hasCoords(branch) ? "Weather location set" : "Add a weather location"}
        </SetupItem>
        {branch.pcwEnabled && (
          <SetupItem done onClick={onEdit}>
            Shop clock board
          </SetupItem>
        )}
      </div>
    </div>
  );
};

// Sentinel Select value for a location that isn't one of the Big C branches
// (e.g. a corporate lobby). Standard picks use the branch name for BOTH the
// display name and GM access, so there's only one thing to choose.
const CUSTOM_LOCATION = "__custom__";

// "41.7434, -86.3131" (what Google Maps copies) → [lat, lng], else null.
const parseLatLng = (text) => {
  const m = String(text || "").match(/^\s*(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)\s*$/);
  return m ? [m[1], m[2]] : null;
};

const DialogSection = ({ title, children }) => (
  <section className="space-y-3">
    <h4 className="app-eyebrow">{title}</h4>
    {children}
  </section>
);

const BranchDialog = ({ branch, stats, userId, onClose, onDone }) => {
  // An existing branch is "standard" when it's simply named after the Big C
  // branch whose GM manages it — anything else opens in custom mode.
  const isStandard =
    branch && BRANCHES.includes(branch.name) && branch.userBranch === branch.name;
  const [selection, setSelection] = useState(
    branch ? (isStandard ? branch.name : CUSTOM_LOCATION) : ""
  );
  const [customName, setCustomName] = useState(
    branch && !isStandard ? branch.name : ""
  );
  const [customUserBranch, setCustomUserBranch] = useState(
    branch && !isStandard ? branch.userBranch || "" : ""
  );
  const [locationLabel, setLocationLabel] = useState(
    branch?.locationLabel || ""
  );
  const [latitude, setLatitude] = useState(
    branch?.latitude != null ? String(branch.latitude) : ""
  );
  const [longitude, setLongitude] = useState(
    branch?.longitude != null ? String(branch.longitude) : ""
  );
  const [pcwEnabled, setPcwEnabled] = useState(!!branch?.pcwEnabled);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const custom = selection === CUSTOM_LOCATION;
  const name = custom ? customName : selection;
  const userBranch = custom ? customUserBranch : selection;
  // The shop clock board shows one shop's own job clock — Priebes' (PCW) or
  // the Mill's — so only those locations offer it (the server enforces the
  // same rule: server/routes/api/PCW/shopClocks.js signageBranch).
  const pcwAllowed = /priebes|\bmill\b/i.test(name);
  const coordsSet = latitude.trim() && longitude.trim();

  // Pasting "lat, lng" (Google Maps' right-click copy) into either box fills
  // both.
  const onCoordPaste = (e) => {
    const pair = parseLatLng(e.clipboardData.getData("text"));
    if (!pair) return;
    e.preventDefault();
    setLatitude(pair[0]);
    setLongitude(pair[1]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name,
        userBranch,
        locationLabel,
        latitude: latitude.trim(),
        longitude: longitude.trim(),
        pcwEnabled: pcwEnabled && pcwAllowed,
      };
      if (branch) await updateSignageBranch(branch.id, payload, userId);
      else await createSignageBranch(payload, userId);
      onDone();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const remove = async () => {
    const warning =
      `Delete "${branch.name}"?\n\n` +
      `This permanently removes the branch, its ${stats?.displays ?? 0} display(s), ` +
      `${stats?.playlists ?? 0} playlist(s), and all of its media. This cannot be undone.`;
    if (!window.confirm(warning)) return;
    try {
      await deleteSignageBranch(branch.id, userId);
      onDone();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={submit}>
        <div className="app-divider border-b px-6 py-4">
          <h3 className="text-lg font-semibold tracking-tight">
            {branch ? `Edit ${branch.name}` : "New signage branch"}
          </h3>
          <p className="app-muted text-sm">
            A branch is one location with its own displays, media, and playlists.
          </p>
        </div>

        <div className="space-y-6 px-6 py-5">
          <DialogSection title="Location & access">
            <FormControl fullWidth size="small">
              <InputLabel id="signage-branch-pick">Branch</InputLabel>
              <Select
                labelId="signage-branch-pick"
                label="Branch"
                value={selection}
                onChange={(e) => setSelection(e.target.value)}
                autoFocus
              >
                {BRANCHES.map((b) => (
                  <MenuItem key={b} value={b}>
                    {b}
                  </MenuItem>
                ))}
                <MenuItem value={CUSTOM_LOCATION}>
                  <em>Custom location…</em>
                </MenuItem>
              </Select>
            </FormControl>
            {!custom && (
              <p className="app-muted -mt-1 text-xs">
                {selection
                  ? `The ${selection} General Manager (plus admins and the Executive Team) can manage its displays.`
                  : "Pick the Big C branch these TVs live at — its General Manager gets access automatically."}
              </p>
            )}
            {custom && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <TextField
                  label="Location name"
                  placeholder="Corporate lobby"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  fullWidth
                  size="small"
                />
                <FormControl fullWidth size="small">
                  <InputLabel id="signage-user-branch">GM access</InputLabel>
                  <Select
                    labelId="signage-user-branch"
                    label="GM access"
                    value={customUserBranch}
                    onChange={(e) => setCustomUserBranch(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Admins only</em>
                    </MenuItem>
                    {BRANCHES.map((b) => (
                      <MenuItem key={b} value={b}>
                        {b} GM
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            )}
            <TextField
              label="Note (optional)"
              placeholder="e.g. Front counter + showroom"
              value={locationLabel}
              onChange={(e) => setLocationLabel(e.target.value)}
              fullWidth
              size="small"
            />
          </DialogSection>

          <DialogSection title="Weather location">
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label="Latitude"
                placeholder="41.7434"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                onPaste={onCoordPaste}
                fullWidth
                size="small"
              />
              <TextField
                label="Longitude"
                placeholder="-86.3131"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                onPaste={onCoordPaste}
                fullWidth
                size="small"
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="app-muted text-xs">
                In Google Maps, right-click the building and click the numbers
                to copy them, then paste here — both boxes fill in.
              </p>
              {coordsSet && (
                <a
                  href={`https://www.google.com/maps?q=${encodeURIComponent(`${latitude.trim()},${longitude.trim()}`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="app-accent inline-flex items-center gap-1 text-xs hover:underline"
                >
                  <MapOutlinedIcon sx={{ fontSize: 14 }} /> Check on map
                  <OpenInNewIcon sx={{ fontSize: 11 }} />
                </a>
              )}
            </div>
          </DialogSection>

          {pcwAllowed && (
            <DialogSection title="Shop floor">
              <label className="app-surface flex cursor-pointer items-center justify-between gap-3 rounded-xl border p-3">
                <span>
                  <span className="block text-sm font-medium">Shop clock board</span>
                  <span className="app-muted block text-xs">
                    Offers the punched-in-workers slide and the live board
                    display mode (on the clock, job hours, this week's
                    schedule, deadlines) to this branch.
                  </span>
                </span>
                <Switch
                  size="small"
                  checked={pcwEnabled}
                  onChange={(e) => setPcwEnabled(e.target.checked)}
                />
              </label>
            </DialogSection>
          )}

          <ErrorNote>{error}</ErrorNote>
        </div>

        <div className="app-divider flex items-center gap-2 border-t px-6 py-3">
          {branch && (
            <Button color="error" size="small" onClick={remove}>
              Delete branch
            </Button>
          )}
          <div className="ml-auto flex gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving || !name.trim()}
            >
              {branch ? "Save" : "Create branch"}
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
};

// Org-wide signage settings, as a status page: the OpenWeather key (with a
// live test) and which branches still lack a location, automatic player
// updates with the fleet's software versions, YouTube import readiness, and
// the USB setup files for a new display.
const SettingsCard = ({ icon, title, ok, status, children }) => (
  <section className="app-surface overflow-hidden rounded-2xl border">
    <header className="app-divider flex flex-wrap items-center gap-2.5 border-b px-4 py-3">
      <span className="app-icon-tile h-8 w-8 rounded-lg bg-sky-500/15 text-sky-600 dark:text-sky-300">
        {icon}
      </span>
      <h4 className="flex-1 text-sm font-semibold">{title}</h4>
      {status && (
        <span
          className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
            ok
              ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
              : "bg-amber-500/15 text-amber-800 dark:text-amber-300"
          }`}
        >
          {ok ? <CheckCircleIcon sx={{ fontSize: 15 }} /> : <ErrorOutlineIcon sx={{ fontSize: 15 }} />}
          {status}
        </span>
      )}
    </header>
    <div className="space-y-3 px-4 py-3.5">{children}</div>
  </section>
);

export const SignageSettingsDialog = ({ userId, branches = [], devices = [], onClose }) => {
  const [settings, setSettings] = useState(null); // null = loading
  const [saved, setSaved] = useState(null); // last loaded/saved values, for dirty
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [test, setTest] = useState(null); // null | "testing" | { ok, ... } | { error }
  const [recheckingYt, setRecheckingYt] = useState(false);

  useEffect(() => {
    fetchSignageSettings(userId)
      .then((s) => {
        setSettings(s);
        setSaved(s);
      })
      .catch((err) => {
        setSettings({ openWeatherApiKey: "", autoUpdateEnabled: true });
        setError(err.message);
      });
  }, [userId]);

  const key = (settings?.openWeatherApiKey || "").trim();
  const dirty =
    !!settings &&
    !!saved &&
    (key !== (saved.openWeatherApiKey || "").trim() ||
      (settings.autoUpdateEnabled !== false) !== (saved.autoUpdateEnabled !== false));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await updateSignageSettings(
        { openWeatherApiKey: key, autoUpdateEnabled: settings.autoUpdateEnabled !== false },
        userId
      );
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const testKey = async () => {
    setTest("testing");
    try {
      setTest(await testSignageWeatherKey(key, userId));
    } catch (err) {
      setTest({ error: err.message });
    }
  };

  const recheckYoutube = async () => {
    setRecheckingYt(true);
    try {
      const fresh = await fetchSignageSettings(userId, { refresh: true });
      setSettings((s) => ({ ...s, youtubeImport: fresh.youtubeImport }));
    } catch (err) {
      setError(err.message);
    } finally {
      setRecheckingYt(false);
    }
  };

  // ---- derived status ----
  const noLocation = branches.filter((b) => !hasCoords(b));
  // Newest version the displays' server hands out (it reports this itself,
  // so it's known even when no display has checked in lately).
  const latest = settings?.offeredClientVersions?.length
    ? [...settings.offeredClientVersions].sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true })
      ).pop()
    : null;
  const versions = useMemo(() => {
    const map = new Map();
    for (const d of devices) {
      const v = d.health?.version || null;
      map.set(v, (map.get(v) || 0) + 1);
    }
    return [...map.entries()].sort((a, b) =>
      String(b[0] || "").localeCompare(String(a[0] || ""), undefined, { numeric: true })
    );
  }, [devices]);
  // Behind = the server offers something newer (a display flashed from a
  // newer image is ahead, not outdated). Count offline displays too — they
  // update when they next check in.
  const isBehind = (v) =>
    !!v && !!latest && latest.localeCompare(v, undefined, { numeric: true }) > 0;
  const outdated = devices.filter((d) => isBehind(d.health?.version)).length;
  const offlineOutdated = devices.filter(
    (d) => !d.online && isBehind(d.health?.version)
  ).length;
  const yt = settings?.youtubeImport;
  const lastChanged =
    saved?.updatedAt &&
    `Last changed ${new Date(saved.updatedAt).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    })}${saved.updatedBy ? ` by ${saved.updatedBy}` : ""}`;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={submit} className="flex max-h-[90vh] flex-col">
        <div className="app-divider flex items-center gap-3 border-b px-6 py-4">
          <span className="app-icon-tile h-10 w-10 rounded-xl bg-violet-500/15 text-violet-600 dark:text-violet-300">
            <SettingsOutlinedIcon fontSize="small" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold tracking-tight">Signage settings</h3>
            <p className="app-muted truncate text-xs">
              Apply to every branch and display{lastChanged ? ` · ${lastChanged}` : ""}
            </p>
          </div>
        </div>

        {settings === null ? (
          <Loading />
        ) : (
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            <SettingsCard
              icon={<WbSunnyOutlinedIcon fontSize="small" />}
              title="Weather"
              ok={!!key && noLocation.length === 0}
              status={
                !key
                  ? "No API key"
                  : noLocation.length
                    ? `${noLocation.length} branch${noLocation.length === 1 ? "" : "es"} without a location`
                    : "Ready"
              }
            >
              <div className="flex items-start gap-2">
                <TextField
                  label="OpenWeather API key"
                  type={showKey ? "text" : "password"}
                  value={settings.openWeatherApiKey ?? ""}
                  onChange={(e) => {
                    setSettings((s) => ({ ...s, openWeatherApiKey: e.target.value }));
                    setTest(null);
                  }}
                  fullWidth
                  size="small"
                  inputProps={{ style: { fontFamily: "monospace" }, autoComplete: "off" }}
                />
                <button
                  type="button"
                  className={`${ICON_BTN_SM} mt-0.5 !h-9 !w-9`}
                  title={showKey ? "Hide key" : "Show key"}
                  aria-label={showKey ? "Hide key" : "Show key"}
                  onClick={() => setShowKey((v) => !v)}
                >
                  {showKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </button>
                <Button
                  size="small"
                  variant="outlined"
                  className="!mt-0.5 shrink-0"
                  disabled={!key || test === "testing"}
                  onClick={testKey}
                >
                  {test === "testing" ? <CircularProgress size={16} /> : "Test key"}
                </Button>
              </div>
              {test && test !== "testing" && (
                <p
                  className={`rounded-xl px-3 py-2 text-xs ${
                    test.ok
                      ? "bg-emerald-500/10 text-emerald-800 dark:text-emerald-300"
                      : "bg-rose-500/10 text-rose-700 dark:text-rose-300"
                  }`}
                >
                  {test.ok
                    ? `The key works — it's ${test.tempF}°F${test.conditions ? ` with ${test.conditions}` : ""} at ${test.place} right now.`
                    : test.error}
                </p>
              )}
              <p className="app-muted text-xs">
                One key serves every branch, and the free tier is plenty. Get one
                at home.openweathermap.org/api_keys.
              </p>
              {branches.length > 0 && (
                <p className={`text-xs ${noLocation.length ? "text-amber-700 dark:text-amber-400" : "app-muted"}`}>
                  {noLocation.length
                    ? `No weather location yet: ${noLocation.map((b) => b.name).join(", ")}. Add one with the pencil on the branch's card (Branches tab).`
                    : `All ${branches.length} branch${branches.length === 1 ? " has" : "es have"} a weather location.`}
                </p>
              )}
            </SettingsCard>

            <SettingsCard
              icon={<SystemUpdateAltIcon fontSize="small" />}
              title="Player software"
              ok={!!latest && outdated === 0}
              status={
                devices.length === 0
                  ? null
                  : !latest
                    ? "Latest version unknown"
                    : outdated
                      ? `${outdated} display${outdated === 1 ? "" : "s"} to update`
                      : "All up to date"
              }
            >
              <label className="flex cursor-pointer items-center justify-between gap-3">
                <span>
                  <span className="block text-sm font-medium">Update players automatically</span>
                  <span className="app-muted block text-xs">
                    Displays with older software update themselves — no need to
                    touch the Pi.
                  </span>
                </span>
                <Switch
                  size="small"
                  checked={settings.autoUpdateEnabled !== false}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, autoUpdateEnabled: e.target.checked }))
                  }
                />
              </label>
              {versions.length > 0 && (
                <div className="app-divider divide-y divide-[color:var(--app-divider)] overflow-hidden rounded-xl border text-xs">
                  {versions.map(([v, n]) => (
                    <div key={v || "none"} className="flex items-center gap-2 px-3 py-2">
                      <span className="font-mono">{v ? `v${v}` : "No report yet"}</span>
                      {v && v === latest && <Pill tone="emerald">latest</Pill>}
                      {isBehind(v) && <Pill tone="sky">update available</Pill>}
                      {v && latest && v !== latest && !isBehind(v) && (
                        <Pill title={`The displays' server only offers v${latest}`}>
                          newer than server
                        </Pill>
                      )}
                      <span className="app-muted ml-auto tabular-nums">
                        {n} display{n === 1 ? "" : "s"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {latest ? (
                <p className="app-muted text-xs">
                  The displays' server offers v{latest}.
                  {offlineOutdated > 0 &&
                    ` ${offlineOutdated} of the displays to update ${offlineOutdated === 1 ? "is" : "are"} offline — ${offlineOutdated === 1 ? "it updates" : "they update"} as soon as ${offlineOutdated === 1 ? "it checks" : "they check"} in.`}
                </p>
              ) : (
                devices.length > 0 && (
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    The displays' server hasn't reported which version it offers
                    yet. It does when it restarts or a display checks in — make
                    sure the player files (media-loop-main) are deployed next to
                    the server and it has been restarted.
                  </p>
                )
              )}
            </SettingsCard>

            {yt && (
              <SettingsCard
                icon={<YouTubeIcon fontSize="small" />}
                title="YouTube import"
                ok={yt.ready}
                status={yt.ready ? "Ready" : "Not set up"}
              >
                <div className="flex items-center gap-3">
                  <p className="min-w-0 flex-1 text-xs">
                    {yt.ready ? (
                      <span className="app-muted">
                        yt-dlp {yt.ytdlp} · ffmpeg {yt.ffmpeg}. If imports start
                        failing, IT can update yt-dlp with “sudo yt-dlp -U”.
                      </span>
                    ) : (
                      <span className="text-amber-700 dark:text-amber-400">{yt.message}</span>
                    )}
                  </p>
                  <Button
                    size="small"
                    startIcon={recheckingYt ? <CircularProgress size={14} /> : <RefreshIcon />}
                    disabled={recheckingYt}
                    onClick={recheckYoutube}
                  >
                    Check again
                  </Button>
                </div>
              </SettingsCard>
            )}

            <SettingsCard icon={<UsbOutlinedIcon fontSize="small" />} title="Set up a new display">
              <p className="text-xs leading-relaxed">
                Flash Raspberry Pi OS Lite (64-bit) to a USB stick or SD card,
                copy everything inside the zip's boot-files folder onto its
                boot drive, and plug it into the Pi. The TV walks you through
                getting it online (Ethernet, Big C Guest / PCW_Guest, or your
                phone), installs itself (10–20 minutes), and then shows a PIN
                to register on the Displays tab.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  href="/boot-files.zip"
                  download
                >
                  Download setup files
                </Button>
                <Button
                  size="small"
                  startIcon={<HelpOutlineIcon />}
                  component={Link}
                  to="/help?a=signage-register-device"
                  onClick={onClose}
                >
                  Registering a display
                </Button>
              </div>
            </SettingsCard>

            <ErrorNote>{error}</ErrorNote>
          </div>
        )}

        <div className="app-divider flex items-center gap-2 border-t px-6 py-3">
          <span className="app-muted text-xs">
            {dirty ? "You have unsaved changes." : ""}
          </span>
          <div className="ml-auto flex gap-2">
            <Button onClick={onClose}>{dirty ? "Cancel" : "Close"}</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving || !dirty}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
};
